import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const RECONCILE_INTERVAL_MS = 5 * 60 * 1000;

// Drives the OpenRouter balance widget. On mount: fetch true balance.
// Subscribe to ai_usage realtime; decrement displayed balance on each new
// row. Every 5 min: re-fetch to reconcile drift.
export function useOpenRouterBalance() {
  const [balance, setBalance] = useState(null); // {total_credits, total_usage, remaining}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reconcileRef = useRef(null);

  const fetchBalance = useCallback(async () => {
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError('not-authenticated');
        return;
      }
      const res = await fetch('/api/admin/openrouter-balance', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.error || `HTTP ${res.status}`);
        return;
      }
      setBalance(await res.json());
    } catch (e) {
      setError(e?.message || 'unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
    reconcileRef.current = setInterval(fetchBalance, RECONCILE_INTERVAL_MS);

    const channel = supabase
      .channel('ai_usage_widget')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ai_usage' },
        (payload) => {
          const costRaw = payload?.new?.cost_usd;
          const cost = typeof costRaw === 'number' ? costRaw : Number(costRaw);
          if (!Number.isFinite(cost)) return;
          setBalance((prev) => prev ? {
            ...prev,
            total_usage: (prev.total_usage || 0) + cost,
            remaining:   (prev.remaining || 0) - cost,
          } : prev);
        },
      )
      .subscribe();

    return () => {
      clearInterval(reconcileRef.current);
      supabase.removeChannel(channel);
    };
  }, [fetchBalance]);

  return { balance, loading, error, refresh: fetchBalance };
}
